/**
 * The layering for a right-side detail drawer.
 *
 * A drawer is a detail beside a list, not a modal over it: the list is still
 * the thing you navigate, the sidebar is still the way out, and Ask is still
 * where you ask about what is open. The page behind is dimmed but nothing
 * traps a click. Esc and the close control are how a drawer closes.
 */

/**
 * Where a drawer is allowed to be: exactly the app's content panel, the same
 * box the Ask panel fills. The sidebar on the left and the Ask panel on the
 * right publish their widths as CSS variables and the drawer stops at both,
 * and `top-2` is the grey strip the panel starts below (Dashboard's `pt-2`),
 * so a drawer runs top to bottom of the panel and not of the window.
 *
 * `pointer-events-none` so the page underneath stays live; the drawer inside
 * sets `pointer-events-auto`. No z-index — each call site adds its own literal
 * one, because a drawer's layer depends on what it has to sit above.
 */
export const DRAWER_BOUNDS_CLASS =
  'pointer-events-none fixed top-2 bottom-0 left-[var(--trodo-rail-w,0px)] right-[var(--trodo-ask-w,0px)] flex justify-end';

/** The bounds at the ordinary drawer layer. */
export const DRAWER_OVERLAY_CLASS = `${DRAWER_BOUNDS_CLASS} z-[60]`;

/**
 * A dim over the page while a drawer is open, so the drawer reads as the thing
 * in front. It takes no clicks (`pointer-events-none`): the list behind stays
 * live, and a click there still swaps the drawer to that row. Esc and the
 * close control are still how a drawer closes. It covers the whole window,
 * sidebar included, so everything behind the drawer sits at one brightness.
 */
// eslint-disable-next-line no-unused-vars
export default function DrawerScrim({ onClose, dismisses = true, className = '' }) {
  return (
    <>
      <div aria-hidden className={`trodo-drawer-dim pointer-events-none fixed inset-0 ${className}`} />
      <style>{`
        .trodo-drawer-dim { background: rgba(20, 19, 18, 0.06); animation: trodoDrawerDim 180ms ease-out; }
        :root[data-theme="dark"] .trodo-drawer-dim { background: rgba(0, 0, 0, 0.22); }
        @keyframes trodoDrawerDim { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .trodo-drawer-dim { animation: none; } }
      `}</style>
    </>
  );
}
